import { useEffect, useReducer, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IpponButton } from '@ippon-ui/react';
import { LoaderComponent, useLoader } from '@/generic/loader';
import { Optional } from '@/generic/functional';
import { type Page, Pagination } from '@/generic/pagination';
import { inject } from '@/rules-management/injections.ts';
import { RULES_REPOSITORY } from '@/rules-management/RulesManagementKeys.ts';
import type { DraftRule } from '@/rules-management/domain/DraftRule.ts';
import { DraftRulesComponent } from '@/rules-management/primary/DraftRulesComponent.tsx';
import { useDraftRulesPublication } from '@/rules-management/primary/UseDraftRulesPublication.ts';
import { useDraftRulesEdition } from '@/rules-management/primary/UseDraftRulesEdition.ts';

const DRAFT_RULES_PAGE_SIZE = 30;
const FIRST_DRAFT_RULES_PAGE = 1;
const DRAFT_RULES_COUNT_PAGINATION = Pagination.of({ page: 1, pageSize: 1 });

const toError = (error: unknown): Error =>
  error instanceof Error ? error : new Error(String(error));

const DraftRulesButton = ({ count, onClick }: { count: number; onClick: () => void }) => {
  const { t } = useTranslation('rules-management');

  return (
    <IpponButton
      color="neutral"
      variant="outline"
      onClick={onClick}
      iconRight={{ name: 'file-tray-full', variant: 'outline' }}
      dataSelector="rules-management.rules.drafts-trigger"
    >
      {t('rules-management.rules.drafts-trigger', { count })}
    </IpponButton>
  );
};

const withoutDraftRulesButton = () => null;

export const DraftRulesTriggerComponent = () => {
  const [open, setOpen] = useState(false);
  const [drafts, setDrafts] = useState<Optional<Page<DraftRule>>>(Optional.empty());
  const [failure, setFailure] = useState<Optional<Error>>(Optional.empty());
  const [refreshToken, refresh] = useReducer((token: number) => token + 1, 0);

  useEffect(() => inject(RULES_REPOSITORY).onDraftRulesChanged(() => refresh()), []);

  const countLoader = useLoader(
    () => inject(RULES_REPOSITORY).listDrafts(DRAFT_RULES_COUNT_PAGINATION),
    [refreshToken],
  );

  const loadDrafts = async (page: number): Promise<void> => {
    try {
      const loaded = await inject(RULES_REPOSITORY).listDrafts(
        Pagination.of({ page, pageSize: DRAFT_RULES_PAGE_SIZE }),
      );
      setDrafts(Optional.of(loaded));
      setFailure(Optional.empty());
    } catch (error) {
      setFailure(Optional.of(toError(error)));
    }
  };

  const handleClose = () => {
    setOpen(false);
    setDrafts(Optional.empty());
    setFailure(Optional.empty());
    resetPublication();
    resetEdition();
    refresh();
  };

  const {
    selection,
    setSelection,
    publicationFailure,
    handlePublish,
    discardFailure,
    handleDiscard,
    reset: resetPublication,
  } = useDraftRulesPublication({
    reloadDrafts: loadDrafts,
    onFullyPublished: handleClose,
  });

  const {
    editFailure,
    handleSave,
    reset: resetEdition,
  } = useDraftRulesEdition({
    reloadDrafts: loadDrafts,
  });

  const handleOpen = () => {
    setOpen(true);
    void loadDrafts(FIRST_DRAFT_RULES_PAGE);
  };

  const onSuccess = (page: Page<DraftRule>) => (
    <DraftRulesButton count={page.totalElementsCount} onClick={handleOpen} />
  );

  return (
    <>
      <LoaderComponent
        loader={countLoader}
        onLoading={withoutDraftRulesButton}
        onError={withoutDraftRulesButton}
        onSuccess={onSuccess}
      />
      <DraftRulesComponent
        open={open}
        drafts={drafts}
        failure={failure}
        selection={selection}
        publicationFailure={publicationFailure}
        discardFailure={discardFailure}
        editFailure={editFailure}
        onSelectionChange={setSelection}
        onPageChange={(page) => void loadDrafts(page)}
        onPublish={handlePublish}
        onDiscard={(draft, page) => void handleDiscard(draft, page)}
        onSave={(draft, edit, page) => handleSave(draft.id, edit, page)}
        onClose={handleClose}
      />
    </>
  );
};
